<template>
  <div class="form-section group">
    <span class="icon">
      <icon-colored-exchange v-if="isCuratorOrganization" />
      <icon-colored-deal v-else />
    </span>
    <span v-if="isNew">{{ capitalize(i(isCuratorOrganization ? 'actions.newCuratedDeal' : 'actions.newDeal')) }}</span>
    <span v-else>{{ capitalize(i(isCuratorOrganization ? 'actions.editCuratedDeal' : 'actions.editDeal')) }}</span>
  </div>
  <div class="form-section-content">
    <!-- NAME -->
    <NameForm
      :entity="deal"
      :errors="errors"
      :property="Package.entityProperties.name" />

    <!-- DEAL ID -->
    <div
      v-if="!isNew"
      class="form-group row">
      <form-label
        for-i-d="edit_package_deal_id"
        :is-required="true"
        >{{ capitalize(i('names.dealID', 1)) }}
      </form-label>
      <div class="col-sm-8">
        <input
          id="edit_package_deal_id"
          v-model="dealID"
          name="name"
          type="text"
          :class="{ 'form-control': true, input: true, 'is-danger': errors.has('name') }" />
        <span
          v-show="errors.has('name')"
          class="help is-danger"
          >{{ errors.first('dealID') }}</span
        >
      </div>
    </div>

    <!-- DEAL SCOPE -->
    <div
      v-show="showDealScope"
      class="form-group row">
      <form-label :is-required="true">{{ capitalize(i('names.dealScope', 1)) }}</form-label>
      <div class="col-sm-8">
        <label
          class="control-label"
          for="edit_package_is_not_flexible">
          <input
            id="edit_package_is_not_flexible"
            v-model="isFlexibleRef"
            type="radio"
            name="isFlexible"
            :value="false" />
          {{ capitalize(i('states.predefined')) }}
        </label>
        <label
          class="control-label ml-3"
          for="edit_package_is_flexible">
          <input
            id="edit_package_is_flexible"
            v-model="isFlexibleRef"
            type="radio"
            name="isFlexible"
            :disabled="isPurchaseTypeCadence"
            :value="true" />
          {{ capitalize(i('states.buyerDefinable')) }}
          <span class="ml-1 badge badge-rounded badge-sm badge-muted">{{ i('messages.internalDSPBuyersOnly') }}</span>
        </label>
      </div>
      <div class="col-sm-1 field-help">
        <div v-tooltip.left="capitalize(i('messages.fieldDescriptionPackageFlexible'))">
          <icon-info />
        </div>
      </div>
    </div>

    <!-- DIRECT BILLING -->
    <div
      v-if="isSupplyOrganization && organizationSettings?.uiEnableDirectBilling"
      class="form-group row">
      <form-label>{{ capitalize(i('messages.directBilling')) }}</form-label>
      <div class="col-sm-8">
        <ToggleButton
          id="edit_package_direct_billing"
          :color="colors.success"
          :value="isDirectBillingEnabled"
          :labels="{ checked: capitalize(i('actions.yes')), unchecked: capitalize(i('actions.no')) }"
          @change="
            () => {
              isDirectBillingEnabled = !isDirectBillingEnabled;
            }
          " />
      </div>
      <div
        v-show="isDirectBillingEnabled"
        class="col-sm-1 field-help">
        <div v-tooltip="capitalize(i('messages.directBillingTooltip'))">
          <icon-info />
        </div>
      </div>
    </div>

    <template
      v-for="packageCustomProperty in packageCustomProperties"
      :key="packageCustomProperty.key">
      <div class="form-group row">
        <form-label :for-i-d="`edit_package_custom_property_${packageCustomProperty.key}`">
          {{ packageCustomProperty.label }}
        </form-label>
        <div
          :id="`edit_package_custom_property_${packageCustomProperty.key}`"
          class="col-sm-8">
          <template v-if="packageCustomProperty.type === 'search'">
            <multiselect
              v-model="packageCustomProperty.value"
              :options="packageCustomProperty.sourceData"
              :multiple="false"
              :close-on-select="true"
              :clear-on-select="true"
              :hide-selected="false"
              :searchable="true"
              :show-labels="false"
              select-label=""
              deselect-label="✕"
              selected-label=""
              track-by="id"
              label="name"
              :placeholder="packageCustomProperty.placeholder"
              :no-options="packageCustomProperty.placeholder"
              @select="(change) => emits('onHandlePackageCustomPropertyChange', packageCustomProperties, change)"
              @search-change="
                (term) => emits('onHandlePackageCustomPropertySearchChange', packageCustomProperties, term)
              ">
              <template #noResult>
                <div v-if="packageCustomProperty.isSearching">
                  <loader-small />
                  {{ capitalize(i('states.searching')) }}
                </div>
                <div v-else>{{ capitalize(i('messages.noResultsFound')) }}</div>
              </template>
              <template #noOptions>
                <span>{{ packageCustomProperty.placeholder }}</span>
              </template>
            </multiselect>
          </template>
          <template v-if="packageCustomProperty.type === 'list'">
            <div v-if="packageCustomProperty.sourceData == null">
              <loader-small />
            </div>
            <multiselect
              v-else
              v-model="packageCustomProperty.value"
              :options="packageCustomProperty.sourceData"
              :multiple="false"
              :close-on-select="true"
              :clear-on-select="true"
              :hide-selected="false"
              :searchable="true"
              :show-labels="false"
              select-label=""
              deselect-label="✕"
              selected-label=""
              track-by="id"
              label="name"
              :placeholder="packageCustomProperty.placeholder"
              @select="(change) => emits('onHandlePackageCustomPropertyChange', packageCustomProperties, change)" />
          </template>
        </div>
      </div>
    </template>

    <!-- HIDE UNIT INFORMATION -->
    <template v-if="showUnitInformationInBidRequests">
      <div class="form-group row">
        <form-label for-i-d="edit_package_hide_screen_info_from_bid_requests">
          <span>{{ capitalize(i('messages.hideScreenInformationInBidRequests', 2)) }}</span>
        </form-label>
        <div
          id="edit_package_hide_screen_info_from_bid_requests"
          class="col-sm-8">
          <ToggleButton
            :color="colors.success"
            :value="hideUnitInformationInBidRequests"
            :labels="{
              checked: capitalize(i('actions.yes')),
              unchecked: capitalize(i('actions.no')),
            }"
            @change="hideUnitInformationInBidRequests = !hideUnitInformationInBidRequests" />
          <span
            v-show="errors.has('hideUnitInformationInBidRequests')"
            class="help is-danger"
            >{{ errors.first('hideUnitInformationInBidRequests') }}</span
          >
        </div>
      </div>
    </template>

    <!-- NOTES -->
    <div class="form-group row">
      <form-label for-i-d="edit_package_notes">{{ capitalize(i('names.notes', 1)) }}</form-label>
      <div class="col-sm-8">
        <textarea
          id="edit_package_notes"
          v-model="notes"
          name="notes"
          :class="{ 'form-control': true, input: true, 'is-danger': errors.has('notes') }" />
        <span
          v-show="errors.has('notes')"
          class="help is-danger"
          >{{ errors.first('notes') }}</span
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, inject, Ref, ref } from 'vue';

  import Colors from '@/Colors';
  import { FormLabel, LoaderSmall } from '@/common/components/controls';
  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import NameForm from '@/common/components/entity-form/NameForm.vue';
  import { IconColoredDeal, IconColoredExchange } from '@/common/icons/colored';
  import { IconInfo } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import { i, useInjected } from '@/globals';
  import { useDealComputedProperties } from '@/org-app/services/computedProperties/DealComputedProperties';
  import { mapOrgAppGetters, mapOrgAppState } from '@/utils/StateMapping';
  import MessageCollection from '@/validator/MessageCollection';

  interface IProps {
    isNew: boolean;
    showDealScope: boolean;
    isFlexible: boolean;
    isPurchaseTypeCadence: boolean;
    showUnitInformationInBidRequests: boolean;
  }

  const { isNew, showDealScope, isFlexible, isPurchaseTypeCadence, showUnitInformationInBidRequests } =
    defineProps<IProps>();

  const deal: Ref<Package> = useInjected<Package>('deal');
  const errors: Ref<MessageCollection> = inject('errors', null);

  const emits = defineEmits([
    'onFlexibilityChanged',
    'onHandlePackageCustomPropertyChange',
    'onHandlePackageCustomPropertySearchChange',
  ]);

  const { isCuratorOrganization, isSupplyOrganization } = mapOrgAppGetters();
  const { organizationSettings } = mapOrgAppState();

  const colors = ref(Colors);

  const { dealID, isDirectBillingEnabled, hideUnitInformationInBidRequests, notes } = useDealComputedProperties(deal);

  const packageCustomProperties = computed(() => deal.value.packageCustomProperties);

  const isFlexibleRef = computed({
    get() {
      return isFlexible;
    },
    set(value) {
      emits('onFlexibilityChanged', value);
    },
  });
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;
  .form-wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
  }

  #app {
    .form-and-stats {
      display: flex;
      flex-direction: row;
    }

    .form-container {
      flex-grow: 1;
    }

    .alert {
      margin-top: 1.6rem;
    }

    .missing-dimensions {
      text-align: center;
      font-size: 1.2rem;
      padding-top: 1rem;
      border-top: 0.1rem solid $borders-light;

      .unit-dimensions-container {
        margin: 1rem 0;
      }
    }

    .multiselect.is-danger,
    .well.is-danger {
      border: 1px solid red;
      border-radius: 5px;
    }

    #xandr_dv_deal_buyer_restrictions {
      .multiselect {
        .multiselect__tags {
          border: 1px solid red;
        }
      }
    }

    .right-arrow {
      display: flex;
      justify-content: center;
      align-items: center;

      svg {
        width: 4em;
        height: 4em;
        fill: #aaaaaa;
      }
    }

    .pane-group {
      label {
        min-width: 36%;
        display: flex;
        flex-direction: column;
      }

      div {
        display: flex;
        width: 100%;
        flex-direction: column;
      }
    }

    .vue-slider-label {
      width: 20rem;
      display: block;
      margin-left: -10rem;
      text-align: center;
      margin-top: 0.6rem;

      &.left {
        text-align: left;
        margin-left: -0.5rem;
      }

      &.right {
        text-align: right;
        margin-left: -19rem;
      }

      .field-help {
        display: inline-block;
        margin-left: 0.3rem;
        top: 0.3rem;
        position: relative;
      }
    }

    .vue-slider-label-marker {
      width: 0.1rem;
      height: 1rem;
      background: #555;
      margin-top: 1.6rem;
      margin-left: 0.3rem;
    }

    .refresh-cadence-info-container {
      display: flex;
      position: fixed;
      right: 5.6rem;
      margin-top: 10.2rem;
      width: 33rem;
      top: 0;
      padding: 1rem;
      font-size: 0.9em;

      .floating-pane {
        .floating-pane-content {
          width: 33rem;
          flex-direction: initial;
          overflow: hidden;

          .icon-container {
            width: 10rem;
            padding: 4rem 1rem;

            svg {
              width: 8rem;
              height: 8rem;
              fill: #fff;
              stroke: #fff;
            }

            color: #fff;
            background-color: var(--primaryColor);
          }

          .update-cadence-msg {
            width: 23rem;

            .header {
              padding: 0.8rem 1rem;

              .left-col {
                width: 80%;
              }

              .right-col {
                width: 20%;
              }
            }

            .body {
              padding: 1.5rem;
            }

            .footer {
              padding: 1.5rem;
            }
          }
        }
      }
    }

    .multiselect {
      .multiselect__tags {
        border: 1px solid red;
      }
    }
  }
</style>
